import React from 'react';
import {
  ParagraphText,
  StackItem,
  StackLayout,
  StatusBanner,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  DEFAULT_ENTITY_LIST_STATUSES,
  EntityStatusValue,
  getEntityInstanceName,
} from '@web-config-app/core';
import type {
  DomainEntity,
  EntityDetail,
  ConfigEntityDetail,
  ListEndpointPaginationLinks,
  EnvironmentKey,
  Nullable,
} from '@web-config-app/core';
import type {
  GetEntityDetailsPathForId,
  AvailableEnvironment,
} from '@web-config-app/core-react';
import {
  useEntityListTableSelectionState,
  useBulkAction,
  useBulkActionsForBaseId,
  BULK_PROMOTE_SELECTED_ACTION,
} from '@web-config-app/core-react';
import { EntityListPageLayout } from '../entity-list-page-layout/entity-list-page-layout.component';
import { EmptyEntityListCard } from '../empty-entity-list-card/empty-entity-list-card.component';
import { LinkableEntityListTable } from '../entity-list-table/linkable-entity-list-table/linkable-entity-list-table.component';
import { EntityListTablePagination } from '../entity-list-table/entity-list-table-pagination/entity-list-table-pagination.component';
import { bannerAnimation } from '../../constants';
import { SearchForm } from '../search-form/search-form.component';
import { EmptySearchCard } from '../empty-search-card/empty-search-card.component';
import { EmptyStateCard } from '../empty-state-card/empty-state-card.component';
import { StatusFilterSelect } from '../status-filter-select/status-filter-select.component';

export type EntityListPageViewProps = {
  domainEntity: DomainEntity;
  displayEmptyCard: boolean;
  emptyCardHeading: string;
  emptyCardDescription: string;
  getEntityDetailsPathForId: GetEntityDetailsPathForId;
  data: Array<ConfigEntityDetail | EntityDetail> | undefined;
  isLoading: boolean;
  pagination: ListEndpointPaginationLinks | undefined;
  availableEnvironments: Nullable<AvailableEnvironment[]>;
  currentAuthoringEnvironment: EnvironmentKey;
  multiEnvironmentAuthoringEnabled: boolean;
  loadPage: (page: string) => void;
  isEntitySingleEnv?: boolean;
  /** What the environment is called in the UI, for the single-environment banner. */
  environmentName: string;
  onSearch: (search?: string) => void;
  isUsingConfigAppBackend?: boolean;
  searchQuery: Nullable<string>;
  statusFilter?: Nullable<string>;
  /** Comma-joined statuses the list shows before the author changes the Status filter. Defaults to `DEFAULT_ENTITY_LIST_STATUSES`. */
  defaultStatusFilter?: string;
  onStatusFilterChange?: (status?: string) => void;
  entityName: string;
  actions: React.ReactNode;
};

export const EntityListPageView = ({
  displayEmptyCard,
  emptyCardHeading,
  emptyCardDescription,
  domainEntity,
  getEntityDetailsPathForId,
  currentAuthoringEnvironment,
  multiEnvironmentAuthoringEnabled,
  data,
  isLoading,
  pagination,
  loadPage,
  availableEnvironments,
  isEntitySingleEnv,
  environmentName,
  onSearch,
  searchQuery,
  statusFilter,
  defaultStatusFilter = DEFAULT_ENTITY_LIST_STATUSES.join(','),
  onStatusFilterChange,
  isUsingConfigAppBackend,
  actions,
  entityName,
}: EntityListPageViewProps) => {
  const { formatMessage } = useIntl();

  const {
    allSelected,
    someSelected,
    selectedCount,
    getItemSelectionState,
    handleEntitySelected,
    handleSelectAllChange,
    selectedEntities,
  } = useEntityListTableSelectionState(data ?? []);

  // One per promotion target. A tenant with several preview environments gets several
  // "Promote to X" actions rather than none, which is what a single lookup for "the other
  // environment" left it with.
  const bulkPromoteSelectedEntities = useBulkActionsForBaseId(
    BULK_PROMOTE_SELECTED_ACTION,
    selectedEntities,
  );

  const bulkPublishSelectedEntities = useBulkAction(
    'bulk-publish-selected-entities',
    selectedEntities,
  );

  const bulkArchiveSelectedEntities = useBulkAction(
    'bulk-archive-selected-entities',
    selectedEntities,
  );

  const bulkDeleteSelectedEntities = useBulkAction(
    'bulk-delete-selected-entities',
    selectedEntities,
  );

  const allSelectedAreDraft =
    selectedEntities.length > 0 &&
    selectedEntities.every(
      (entity) =>
        entity.attributes.entityMetadata?.status === EntityStatusValue.Draft,
    );

  const allSelectedAreArchived =
    selectedEntities.length > 0 &&
    selectedEntities.every(
      (entity) =>
        entity.attributes.entityMetadata?.status === EntityStatusValue.Archived,
    );

  React.useEffect(() => {
    // reset selection state on page change
    if (pagination?.self) {
      handleSelectAllChange(false);
    }
  }, [pagination?.self, handleSelectAllChange]);

  const hasNonDefaultStatusFilter =
    !!statusFilter && statusFilter !== defaultStatusFilter;
  const showEmptySearchResultsCard = searchQuery && displayEmptyCard;
  const showEmptyFilterResultsCard =
    !searchQuery && hasNonDefaultStatusFilter && displayEmptyCard && !isLoading;
  const showEmptyDataResultsCard =
    !searchQuery &&
    !hasNonDefaultStatusFilter &&
    displayEmptyCard &&
    !isLoading;

  return (
    <EntityListPageLayout
      entityName={entityName}
      availableEnvironments={availableEnvironments}
      currentAuthoringEnvironment={currentAuthoringEnvironment}
      multiEnvironmentAuthoringEnabled={multiEnvironmentAuthoringEnabled}
      isEntitySingleEnv={isEntitySingleEnv}
      actions={actions}
      statusBanners={
        isEntitySingleEnv && (
          <StatusBanner
            data-testid="entity-single-environment-banner"
            status="info"
            css={{ animation: bannerAnimation }}
          >
            <ParagraphText>
              {formatMessage(
                { id: 'ENTITY_SINGLE_ENVIRONMENT_STATUS_BANNER' },
                {
                  environment: environmentName,
                },
              )}
            </ParagraphText>
          </StatusBanner>
        )
      }
    >
      {showEmptyDataResultsCard ? (
        <EmptyEntityListCard
          heading={emptyCardHeading}
          description={emptyCardDescription}
        />
      ) : (
        <>
          {isUsingConfigAppBackend && (
            <StackItem
              css={{
                flexDirection: 'row',
                gap: '$one',
                paddingBlockEnd: '$one',
              }}
            >
              <SearchForm
                id="entity-list-search"
                label={formatMessage({ id: 'ENTITY_SEARCH' }, { entityName })}
                onSearchSubmit={onSearch}
                initialValue={searchQuery || undefined}
              />
              {onStatusFilterChange && (
                <StatusFilterSelect
                  currentStatus={statusFilter || null}
                  onStatusChange={onStatusFilterChange}
                />
              )}
            </StackItem>
          )}
          {showEmptySearchResultsCard && <EmptySearchCard />}
          {showEmptyFilterResultsCard && (
            <EmptyStateCard
              hideImage
              backgroundColor="$surfaceBackgroundSecondary"
            >
              <StackLayout horizontalAlignment="center">
                <UtilityText emphasis="emphasized">
                  {formatMessage({ id: 'NO_FILTER_RESULTS_FOUND' })}
                </UtilityText>
                <UtilityText emphasis="subtle">
                  {formatMessage({
                    id: 'NO_FILTER_RESULTS_FOUND_DESCRIPTION',
                  })}
                </UtilityText>
              </StackLayout>
            </EmptyStateCard>
          )}
          {!showEmptySearchResultsCard && !showEmptyFilterResultsCard && (
            <LinkableEntityListTable
              getItemName={(d: EntityDetail) =>
                getEntityInstanceName(d, domainEntity)
              }
              getItemLinkUrl={getEntityDetailsPathForId}
              entityData={data}
              isLoading={isLoading}
              domainEntity={domainEntity}
              selectionMode="multi"
              selectionProps={{
                selectedEntities,
                onSelectAllChange: (checked) => {
                  handleSelectAllChange(checked);
                },
                getItemSelectionState,
                onEntitySelected: handleEntitySelected,
                allSelected,
                someSelected,
                selectedCount,
              }}
              bulkActions={[
                ...bulkPromoteSelectedEntities,
                bulkPublishSelectedEntities
                  ? {
                      ...bulkPublishSelectedEntities,
                      disabledTooltip: allSelectedAreDraft
                        ? undefined
                        : formatMessage({
                            id: 'BULK_PUBLISH_REQUIRES_DRAFT',
                          }),
                    }
                  : undefined,
                bulkArchiveSelectedEntities
                  ? {
                      ...bulkArchiveSelectedEntities,
                      disabledTooltip: allSelectedAreDraft
                        ? undefined
                        : formatMessage({
                            id: 'BULK_ARCHIVE_REQUIRES_DRAFT',
                          }),
                    }
                  : undefined,
                bulkDeleteSelectedEntities
                  ? {
                      ...bulkDeleteSelectedEntities,
                      disabledTooltip: allSelectedAreArchived
                        ? undefined
                        : formatMessage({
                            id: 'BULK_DELETE_REQUIRES_ARCHIVED',
                          }),
                    }
                  : undefined,
              ].filter((action): action is NonNullable<typeof action> =>
                Boolean(action),
              )}
            />
          )}
          {!showEmptySearchResultsCard &&
            !showEmptyFilterResultsCard &&
            pagination && (
              <EntityListTablePagination
                pagination={pagination}
                loadPage={loadPage}
              />
            )}
        </>
      )}
    </EntityListPageLayout>
  );
};
